/**
 * Element Fingerprinting for Persistent Hint Anchoring
 *
 * Derives stable, deterministic fingerprints from SnapshotElements so that
 * hints can persist across sessions even though snapshot refs are ephemeral.
 * Also provides stable-parent traversal to anchor hints on structural elements
 * rather than data-dependent ones.
 *
 * @module
 */

import * as crypto from 'crypto';
import type { SnapshotElement } from './snapshot-analyzer.js';

// ─── URL Normalization ────────────────────────────────────────────────────────

/**
 * Normalize a URL for hint scoping — collapses numeric/UUID path segments
 * to `:id` and strips query strings so that `/settings/abc-123?tab=general`
 * and `/settings/def-456` both map to `/settings/:id`.
 *
 * Mirrors the normalizeUrl logic in apps/api/src/ai/crawler/url.ts.
 */
export function normalizeUrlForHints(url: string): string {
  try {
    const u = new URL(url);
    const segs = u.pathname.split('/').map((s) => s.trim());
    const mapped = segs.map((s) =>
      /^[0-9]{3,}$/.test(s) || /^[0-9a-fA-F-]{16,}$/.test(s) ? ':id' : s
    );
    const path = mapped.join('/').replace(/\/:id\/:id/g, '/:id');
    return `${u.origin}${path}`;
  } catch {
    return url;
  }
}

/**
 * A fingerprint that uniquely identifies a DOM element across snapshots.
 * Composed of the most stable signals available from the accessibility tree.
 */
export interface ElementFingerprint {
  /** ARIA role (button, link, textbox, navigation, etc.) */
  role: string;
  /** Accessible name — the text content or label */
  name: string | null;
  /** aria-label attribute if present */
  ariaLabel: string | null;
  /** Heading-based ancestor path (e.g., "navigation > Settings > Account") */
  ancestorPath: string | null;
}

/**
 * Compute a fingerprint for a SnapshotElement.
 *
 * Uses role + accessible name + aria-label + ancestor path to produce
 * a deterministic identity. The fingerprint is designed to be stable across
 * snapshots of the same page — the same button with the same label in the
 * same structural location will produce the same fingerprint.
 */
export function computeFingerprint(
  element: SnapshotElement,
  ancestorPath: string | null
): ElementFingerprint {
  return {
    role: element.role,
    name: element.text ?? null,
    ariaLabel: element.attributes['aria-label'] ?? null,
    ancestorPath,
  };
}

/**
 * Compute a deterministic SHA-256 hash of a fingerprint for DB lookup.
 * The hash is stable across runs given the same inputs.
 */
export function hashFingerprint(fp: ElementFingerprint): string {
  const canonical = JSON.stringify([fp.role, fp.name, fp.ariaLabel, fp.ancestorPath]);
  return crypto.createHash('sha256').update(canonical).digest('hex');
}

// ─── Stable Parent Anchoring ──────────────────────────────────────────────────

/**
 * ARIA roles that indicate structural stability — elements with these roles
 * are unlikely to be data-dependent and make good hint anchors.
 */
const STABLE_ROLES = new Set([
  'navigation',
  'main',
  'banner',
  'complementary',
  'dialog',
  'alertdialog',
  'form',
  'region',
  'toolbar',
  'menu',
  'menubar',
  'tablist',
  'tab',
  'tabpanel',
  'heading',
  'contentinfo',
]);

/**
 * Check if a SnapshotElement qualifies as a "stable" anchor for hints.
 *
 * Stable means the element is structural (landmark, heading, toolbar, etc.)
 * and likely to persist across page loads regardless of data changes.
 */
export function isStableElement(element: SnapshotElement): boolean {
  return STABLE_ROLES.has(element.role);
}

/**
 * Find a SnapshotElement by its ref in a tree, returning the element
 * and the path of ancestors leading to it.
 */
export function findElementWithAncestors(
  elements: SnapshotElement[],
  targetRef: string
): { element: SnapshotElement; ancestors: SnapshotElement[] } | null {
  const path: SnapshotElement[] = [];

  function search(nodes: SnapshotElement[]): { element: SnapshotElement; ancestors: SnapshotElement[] } | null {
    for (const node of nodes) {
      if (node.ref === targetRef) {
        return { element: node, ancestors: [...path] };
      }
      path.push(node);
      const found = search(node.children);
      if (found) return found;
      path.pop();
    }
    return null;
  }
  return search(elements);
}

/**
 * Find the nearest stable ancestor for a given element in the tree.
 * If the element itself is stable, returns it directly.
 * Falls back to the root element if no stable ancestor is found.
 */
export function findStableAncestor(
  element: SnapshotElement,
  ancestors: SnapshotElement[]
): SnapshotElement {
  // If the element itself is stable, use it directly
  if (isStableElement(element)) return element;

  // Walk ancestors from nearest to farthest
  for (let i = ancestors.length - 1; i >= 0; i--) {
    if (isStableElement(ancestors[i])) {
      return ancestors[i];
    }
  }

  // Fallback: use root (first ancestor) or the element itself
  return ancestors.length > 0 ? ancestors[0] : element;
}

/**
 * Build the heading-based ancestor path for an element.
 * Produces strings like "navigation > Settings > Account" by collecting
 * headings and named landmarks along the ancestor chain.
 */
export function buildAncestorPath(ancestors: SnapshotElement[]): string | null {
  const segments: string[] = [];

  for (const ancestor of ancestors) {
    if (ancestor.role === 'heading' && ancestor.text) {
      segments.push(ancestor.text);
    } else if (STABLE_ROLES.has(ancestor.role)) {
      const label = ancestor.text || ancestor.attributes['aria-label'];
      if (label) {
        segments.push(label);
      } else {
        segments.push(ancestor.role);
      }
    }
  }

  return segments.length > 0 ? segments.join(' > ') : null;
}

/**
 * Compute the fingerprint for a hint target, anchoring to the nearest
 * stable parent if the target itself is not stable.
 *
 * Returns the anchor element (which may differ from the target), the
 * fingerprint, and the hash.
 */
export function fingerprintForHint(
  elements: SnapshotElement[],
  targetRef: string
): {
  anchor: SnapshotElement;
  target: SnapshotElement;
  fingerprint: ElementFingerprint;
  hash: string;
  anchoredToParent: boolean;
} | null {
  const found = findElementWithAncestors(elements, targetRef);
  if (!found) return null;

  const { element: target, ancestors } = found;
  const anchor = findStableAncestor(target, ancestors);
  const anchoredToParent = anchor !== target;

  // Build ancestor path up to (but not including) the anchor
  const anchorIndex = ancestors.indexOf(anchor);
  const pathAncestors =
    anchorIndex >= 0 ? ancestors.slice(0, anchorIndex + 1) : ancestors;
  const ancestorPath = buildAncestorPath(pathAncestors);

  const fingerprint = computeFingerprint(anchor, ancestorPath);
  const hash = hashFingerprint(fingerprint);

  return { anchor, target, fingerprint, hash, anchoredToParent };
}

// ─── Batch Fingerprinting ─────────────────────────────────────────────────────

/**
 * Compute fingerprints for all elements in a snapshot tree in a single pass.
 * Returns a map from fingerprint hash to the element ref and fingerprint.
 *
 * Used during snapshot formatting to match stored hints to current elements.
 */
export function fingerprintAllElements(
  elements: SnapshotElement[]
): Map<string, { ref: string; fingerprint: ElementFingerprint }> {
  const result = new Map<string, { ref: string; fingerprint: ElementFingerprint }>();

  const ancestors: SnapshotElement[] = [];

  function traverse(nodes: SnapshotElement[]): void {
    for (const node of nodes) {
      if (node.ref) {
        const ancestorPath = buildAncestorPath(ancestors);
        const fp = computeFingerprint(node, ancestorPath);
        const hash = hashFingerprint(fp);
        // First match wins — if two elements produce the same fingerprint,
        // only the first one gets the hint (avoids ambiguity)
        if (!result.has(hash)) {
          result.set(hash, { ref: node.ref, fingerprint: fp });
        }
      }
      ancestors.push(node);
      traverse(node.children);
      ancestors.pop();
    }
  }

  traverse(elements);
  return result;
}
